/**
 * The record an upload leaves behind when the Keycloak session dies under it (AKL-891).
 *
 * **Why this exists.** When Keycloak refuses a token refresh, `keycloak-js` runs
 * `clearToken()`, which fires `onAuthLogout` and then — under `onLoad: "login-required"` —
 * `login()`: a whole-document navigation to the sign-in page. The in-memory file list, the
 * upload manager and every module-scope variable go with it. Nothing told the user an upload
 * had been running; after sign-in the deployment page said "No upload in progress". So the
 * provider keeps a small snapshot of the upload in progress here, and the `onAuthLogout`
 * event writes it to storage in the moment between the refusal and the navigation.
 *
 * **Why `localStorage`, not `sessionStorage`.** The record has to outlive a cross-origin
 * round trip to Keycloak and back, and a user who closes the sign-in tab and opens akili
 * afresh must still be told. `lastFailedRequest.ts` chose `sessionStorage` because its
 * record is about one tab's session; this one is about the user's work. The key pattern is
 * listed in CLAUDE.md's storage-keys table.
 *
 * **Why a module-level snapshot, and why the `onEvent` stream.** The Keycloak event fires
 * from an XHR callback, outside React. And `keycloak.onAuthLogout` itself belongs to
 * `@react-keycloak/core`, which assigns it in `init()` and re-emits every event through the
 * provider's `onEvent` prop — `KeycloakWrapper` hands that stream to `handleKeycloakEvent`
 * rather than overwriting a hook the library owns.
 *
 * **Why a sign-out keeps these keys** (review cycle 2). The three places that sign a user
 * out — `LogoutButton`, `UnauthorizedComponent`'s Okay, and `authHeader()` when it finds no
 * token — used to run a blanket `localStorage.clear()`. On a shared machine that let user B
 * signing out erase the record user A had not yet come back to read, contradicting the
 * account scoping. They now go through `clearLocalStorageOnSignOut()`, which clears
 * everything else and keeps the records that are still valid. So that a record whose owner
 * never returns — an account removed, a browser passed on — does not live in the browser
 * for ever, a record older than `INTERRUPTED_UPLOAD_MAX_AGE_MS` is dropped wherever it is
 * next read.
 *
 * **Why one key per account and deployment** (review cycles 3 and 4). A single slot let
 * user B losing a session mid-upload overwrite the record user A had not yet read; one slot
 * per account still let the same account's two uploads, running in two tabs to two
 * deployments, overwrite each other when the session expired under both — and a shared map
 * updated by read-modify-write let two tabs' logout handlers each read the old map and the
 * later write drop the other's entry. Each record therefore has its own key,
 * `interruptedUpload:<subject>:<deploymentId>`: a write touches nothing else, so there is
 * nothing to race, and each deployment's panel shows and clears only its own record.
 *
 * Every storage access is wrapped in try/catch: a full, disabled or partitioned storage must
 * never turn a lost session into a second failure.
 */

import { getKeycloak } from "../../keycloak";

/**
 * The prefix of every record's `localStorage` key; the full key is
 * `interruptedUpload:<subject>:<deploymentId>` (see `interruptedUploadStorageKey`). Listed
 * in CLAUDE.md's storage-keys table.
 */
export const INTERRUPTED_UPLOAD_STORAGE_KEY_PREFIX = "interruptedUpload:";

/**
 * How long a record is kept for its owner to come back and read (review cycle 2). Long
 * enough for a field trip; short enough that a record nobody will ever read — its account
 * removed, the browser handed to someone else — does not outlive every sign-out for ever.
 * By then the deployment's History has long shown the interrupted upload as ended.
 */
export const INTERRUPTED_UPLOAD_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/** What the provider knows about the upload in progress, kept current while it runs. */
export type ActiveUploadSnapshot = {
  deploymentId: number;
  deploymentGuid: string;
  deploymentName: string;
  /** Files that arrived — uploaded, not skipped. */
  sentFileCount: number;
  /** Files in the selection. */
  selectedFileCount: number;
};

/**
 * The stored record: the snapshot, whose upload it was, and when the session was lost
 * (ISO-8601 UTC). `subject` is the Keycloak subject of the signed-in user — `localStorage`
 * is shared by every account that uses this browser, so a record is only ever read, shown
 * or cleared by the account it belongs to (review cycle 1). It is captured when the
 * snapshot is taken, not when the record is written: `clearToken()` has already dropped
 * `keycloak.subject` by the time `onAuthLogout` fires.
 */
export type InterruptedUpload = ActiveUploadSnapshot & {
  subject: string;
  interruptedAt: string;
};

/**
 * The `localStorage` key of one account's record for one deployment.
 * @param subject the owner's Keycloak subject
 * @param deploymentId the deployment the upload was to
 */
export const interruptedUploadStorageKey = (
  subject: string,
  deploymentId: number,
): string =>
  `${INTERRUPTED_UPLOAD_STORAGE_KEY_PREFIX}${subject}:${deploymentId}`;

let activeUpload: (ActiveUploadSnapshot & { subject: string }) | null = null;

/** The signed-in user's Keycloak subject; "" while there is none. */
const currentSubject = (): string => getKeycloak().subject ?? "";

/**
 * Keeps this module's picture of the upload in progress current. The provider calls it
 * whenever the deployment or the counts change, and with `null` when the upload ends or the
 * provider unmounts — a paused or stopped upload is still in progress here, since its file
 * list would be lost just the same. The signed-in user's subject is attached here, while
 * Keycloak still holds it.
 * @param snapshot the upload in progress, or `null` when there is none
 */
export const setActiveUpload = (
  snapshot: ActiveUploadSnapshot | null,
): void => {
  activeUpload = snapshot ? { ...snapshot, subject: currentSubject() } : null;
};

/**
 * Writes one record under its own key. A storage that cannot be written is logged and
 * otherwise left alone.
 * @param record the record to store
 */
const writeRecord = (record: InterruptedUpload): void => {
  try {
    localStorage.setItem(
      interruptedUploadStorageKey(record.subject, record.deploymentId),
      JSON.stringify(record),
    );
  } catch (error) {
    console.warn("Could not record the interrupted upload:", error);
  }
};

/**
 * Writes the upload in progress, if any, to storage — under the owner's and deployment's
 * own key, so it overwrites nothing but an older record of the same upload and races with
 * nothing another tab writes (review cycles 3 and 4). Called from the `onAuthLogout` event,
 * synchronously, before `keycloak-js` navigates away. Nothing is written when no upload is
 * in progress.
 */
export const recordInterruptedUploadOnLogout = (): void => {
  if (!activeUpload) {
    return;
  }
  writeRecord({ ...activeUpload, interruptedAt: new Date().toISOString() });
};

/**
 * Receives every event `ReactKeycloakProvider` emits and acts on the one that matters here:
 * `onAuthLogout`, raised from the `onAuthLogout` hook `@react-keycloak/core` owns on the
 * Keycloak instance, inside `clearToken()` and before `login()` navigates.
 * @param event the react-keycloak event name
 */
export const handleKeycloakEvent = (event: string): void => {
  if (event === "onAuthLogout") {
    recordInterruptedUploadOnLogout();
  }
};

const isRecord = (value: unknown): value is InterruptedUpload => {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.deploymentId === "number" &&
    typeof candidate.deploymentGuid === "string" &&
    typeof candidate.deploymentName === "string" &&
    typeof candidate.sentFileCount === "number" &&
    typeof candidate.selectedFileCount === "number" &&
    typeof candidate.subject === "string" &&
    typeof candidate.interruptedAt === "string"
  );
};

/** Whether the record is past `INTERRUPTED_UPLOAD_MAX_AGE_MS`; an unreadable date counts as expired. */
const isExpired = (record: InterruptedUpload): boolean => {
  const at = Date.parse(record.interruptedAt);
  return Number.isNaN(at) || Date.now() - at > INTERRUPTED_UPLOAD_MAX_AGE_MS;
};

/** Every `localStorage` key under this module's prefix; `[]` when storage cannot be read. */
const recordKeys = (): string[] => {
  const keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (
        key !== null &&
        key.startsWith(INTERRUPTED_UPLOAD_STORAGE_KEY_PREFIX)
      ) {
        keys.push(key);
      }
    }
  } catch {
    return [];
  }
  return keys;
};

/**
 * Every account's record still worth keeping. A key whose value is not a record, does not
 * describe the account and deployment its key names, or has expired is removed here, so a
 * stale record leaves the browser the next time anyone looks — not only when its owner
 * does.
 */
const readStoredRecords = (): InterruptedUpload[] => {
  const kept: InterruptedUpload[] = [];
  for (const key of recordKeys()) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(localStorage.getItem(key) ?? "");
    } catch {
      parsed = undefined;
    }
    if (
      isRecord(parsed) &&
      interruptedUploadStorageKey(parsed.subject, parsed.deploymentId) ===
        key &&
      !isExpired(parsed)
    ) {
      kept.push(parsed);
    } else {
      try {
        localStorage.removeItem(key);
      } catch {
        // a key that cannot be removed will be skipped again next time
      }
    }
  }
  return kept;
};

/**
 * The signed-in user's record for `deploymentId`, or `null` when there is none, it cannot
 * be read as one, or it has expired. Another account's record is never returned. Does not
 * remove a live record: the deployment's upload panel shows the notice until the user
 * dismisses it or starts a new upload there.
 * @param deploymentId the deployment whose panel is asking
 */
export const readInterruptedUpload = (
  deploymentId: number,
): InterruptedUpload | null => {
  const subject = currentSubject();
  return (
    readStoredRecords().find(
      (record) =>
        record.subject === subject && record.deploymentId === deploymentId,
    ) ?? null
  );
};

/**
 * Removes the signed-in user's record for `deploymentId` and nothing else: not their
 * records for other deployments, and never another account's — those wait for their owner
 * or for their age limit.
 * @param deploymentId the deployment whose notice was dismissed or whose upload restarted
 */
export const clearInterruptedUpload = (deploymentId: number): void => {
  try {
    localStorage.removeItem(
      interruptedUploadStorageKey(currentSubject(), deploymentId),
    );
  } catch {
    // nothing to do: a storage that cannot be written cannot hold a record either
  }
};

/**
 * What a sign-out does to `localStorage` (review cycle 2): removes every key the blanket
 * `localStorage.clear()` it replaces would have, except the interrupted-upload records —
 * whoever they belong to. Each is a message to the account whose upload was lost, and
 * another account signing out on a shared browser must not be the reason it is never
 * delivered. Invalid and expired records are pruned on the way (`readStoredRecords`).
 *
 * The record keys are left in place rather than read, cleared and written back (review
 * cycle 5): a record another tab writes between the read and the `clear()` would have been
 * lost with no snapshot to restore it from. Removing only the other keys touches no record
 * at all, so there is nothing to race.
 */
export const clearLocalStorageOnSignOut = (): void => {
  readStoredRecords(); // prunes records that are unreadable or expired
  const others: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (
        key !== null &&
        !key.startsWith(INTERRUPTED_UPLOAD_STORAGE_KEY_PREFIX)
      ) {
        others.push(key);
      }
    }
  } catch {
    return; // a storage that cannot be read holds nothing this sign-out can reach
  }
  for (const key of others) {
    try {
      localStorage.removeItem(key);
    } catch {
      // a key that cannot be removed is left; the next sign-out tries again
    }
  }
};
